import { useEffect, useRef, useCallback } from 'react';

// Create notification sounds using Web Audio API
class NotificationSoundManager {
  constructor() {
    this.audioContext = null;
    this.initialized = false;
    this.soundPreference = 'chime'; // 'chime', 'alert', or 'off'
  }

  async init() {
    if (this.initialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.error('Web Audio API not supported:', e);
    }
  }

  setSoundPreference(preference) {
    this.soundPreference = preference; // 'chime', 'alert', or 'off'
  }

  async playNotificationSound(type = 'default') {
    if (this.soundPreference === 'off') return;
    
    await this.init();
    
    if (!this.audioContext) return;

    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Use alert-style sounds if preference is 'alert'
    if (this.soundPreference === 'alert') {
      this.playAlertSound(type);
      return;
    }

    // Default: subtle chime sounds
    this.playChimeSound(type);
  }

  playChimeSound(type) {
    // Subtle, professional chime sounds
    switch (type) {
      case 'shift_filled':
      case 'job_picked':
      case 'high':
        // Pleasant ascending chime for job acceptance
        this.playChime(523, 0.12); // C5
        setTimeout(() => this.playChime(659, 0.12), 120); // E5
        setTimeout(() => this.playChime(784, 0.15), 240); // G5
        break;
      
      case 'new_application':
        // Soft double chime
        this.playChime(587, 0.12); // D5
        setTimeout(() => this.playChime(784, 0.15), 150); // G5
        break;
      
      case 'message':
        // Soft single ping for messages
        this.playChime(523, 0.1); // C5
        break;
      
      case 'payment':
        // Pleasant cash sound
        this.playChime(659, 0.1); // E5
        setTimeout(() => this.playChime(784, 0.12), 100); // G5
        break;
      
      case 'shift_cancelled':
        // Descending tone
        this.playChime(523, 0.15); // C5
        setTimeout(() => this.playChime(440, 0.15), 150); // A4
        break;
      
      case 'reminder':
        // Gentle reminder chime
        this.playChime(659, 0.1); // E5
        setTimeout(() => this.playChime(659, 0.1), 300); // E5
        break;
      
      default:
        // Default notification
        this.playChime(523, 0.12); // C5
        setTimeout(() => this.playChime(659, 0.1), 150); // E5
    }
  }

  playAlertSound(type) {
    // More attention-grabbing alert tones
    switch (type) {
      case 'shift_filled':
      case 'job_picked':
      case 'high':
        // Exciting triple tone
        this.playTone(440, 0.15, 'triangle'); // A4
        setTimeout(() => this.playTone(554, 0.15, 'triangle'), 150); // C#5
        setTimeout(() => this.playTone(659, 0.2, 'triangle'), 300); // E5
        setTimeout(() => this.playTone(880, 0.25, 'triangle'), 450); // A5
        break;
      
      case 'new_application':
        // Double alert
        this.playTone(587, 0.15, 'triangle'); // D5
        setTimeout(() => this.playTone(880, 0.2, 'triangle'), 200); // A5
        break;
      
      case 'message':
        // Alert ping
        this.playTone(698, 0.15, 'triangle'); // F5
        break;
      
      case 'payment':
        // Celebratory sound
        this.playTone(523, 0.1, 'triangle'); // C5
        setTimeout(() => this.playTone(659, 0.1, 'triangle'), 100); // E5
        setTimeout(() => this.playTone(784, 0.15, 'triangle'), 200); // G5
        break;
      
      case 'shift_cancelled':
        // Warning tone
        this.playTone(392, 0.2, 'sawtooth'); // G4
        setTimeout(() => this.playTone(294, 0.25, 'sawtooth'), 200); // D4
        break;
      
      case 'reminder':
        // Attention-grabbing reminder
        this.playTone(698, 0.12, 'triangle'); // F5
        setTimeout(() => this.playTone(698, 0.12, 'triangle'), 200); // F5
        setTimeout(() => this.playTone(698, 0.12, 'triangle'), 400); // F5
        break;
      
      default:
        this.playTone(587, 0.15, 'triangle'); // D5
        setTimeout(() => this.playTone(784, 0.15, 'triangle'), 150); // G5
    }
  }

  playChime(frequency, duration) {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    
    // Smooth fade in
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.25, this.audioContext.currentTime + 0.02);
    
    // Smooth fade out
    gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  playTone(frequency, duration, waveType = 'sine') {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.type = waveType;
    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    
    // Snappier attack for alert sounds
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.35, this.audioContext.currentTime + 0.01);
    
    // Fade out
    gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
    
    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }
}

// Singleton instance
const soundManager = new NotificationSoundManager();

// React hook for notification sounds
export function useNotificationSound() {
  const playSound = useCallback(async (type = 'default') => {
    try {
      await soundManager.playNotificationSound(type);
    } catch (e) {
      console.error('Error playing notification sound:', e);
    }
  }, []);

  const setSoundPreference = useCallback((preference) => {
    soundManager.setSoundPreference(preference);
  }, []);

  return { playSound, setSoundPreference };
}

// Function to play sound directly (for use outside React components)
export async function playNotificationSound(type = 'default') {
  try {
    await soundManager.playNotificationSound(type);
  } catch (e) {
    console.error('Error playing notification sound:', e);
  }
}

// Function to set sound preference
export function setSoundPreference(preference) {
  soundManager.setSoundPreference(preference);
}

export default soundManager;
